import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DietProvider } from "@/contexts/DietContext";
import { Sentry } from "@/lib/sentry";
import { toast } from "sonner";
import Login from "./pages/Login";
import Register from "./pages/Register";
import RegisterConfirmation from "./pages/RegisterConfirmation";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import DietBuilder from "./pages/DietBuilder";
import Plans from "./pages/Plans";
import PaymentPending from "./pages/PaymentPending";
import MyDiet from "./pages/MyDiet";
import Diary from "./pages/Diary";
import Metrics from "./pages/Metrics";
import Profile from "./pages/Profile";
import RecipeCalculator from "./pages/RecipeCalculator";
import Recipes from "./pages/Recipes";
import Materiais from "./pages/Materiais";
import Comunidade from "./pages/Comunidade";
import PurchaseHistory from "./pages/PurchaseHistory";
import AppLayout from "./components/AppLayout";
import ScrollToTop from "./components/ScrollToTop";
import MetaPixelTracker from "./components/MetaPixelTracker";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const FallbackComponent = () => (
  <div className="flex min-h-screen items-center justify-center bg-background p-6">
    <div className="text-center space-y-4 max-w-md">
      <h1 className="text-2xl font-bold text-foreground">Ops! Algo deu errado</h1>
      <p className="text-muted-foreground">
        Ocorreu um erro inesperado. Tente recarregar a página.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-primary-foreground font-medium hover:bg-primary/90 transition"
      >
        Recarregar página
      </button>
    </div>
  </div>
);

const App = () => {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      toast.error("Ocorreu um erro inesperado. Tente novamente.");
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={FallbackComponent}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <DietProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <MetaPixelTracker />
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/register/confirmation" element={<RegisterConfirmation />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/pagamento/pendente" element={<PaymentPending />} />
                  <Route element={<AppLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/diet-builder" element={<DietBuilder />} />
                    <Route path="/plans" element={<Plans />} />
                    <Route path="/my-diet" element={<MyDiet />} />
                    <Route path="/diary" element={<Diary />} />
                    <Route path="/metrics" element={<Metrics />} />
                    <Route path="/recipe-calculator" element={<RecipeCalculator />} />
                    <Route path="/recipes" element={<Recipes />} />
                    <Route path="/materiais" element={<Materiais />} />
                    <Route path="/comunidade" element={<Comunidade />} />
                    <Route path="/purchase-history" element={<PurchaseHistory />} />
                    <Route path="/profile" element={<Profile />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </DietProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
